import { __decorate } from "tslib";
import { html, css, LitElement, nothing } from 'lit';
import { customElement, property, queryAssignedElements, } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { MenuEvent } from './viedoc-menu.js';
import { Size } from './enums/size.js';
import { menuItem as menuItemStyle } from './styles/menu-item.js';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { Large, ...MenuItemSize } = Size;
export { MenuItemSize };
let ViedocMenuItem = class ViedocMenuItem extends LitElement {
    constructor() {
        super(...arguments);
        this.checkMark = false;
        this.disabled = false;
        this.secondary = false;
        this.selected = false;
        this.size = MenuItemSize.Medium;
        this.value = '';
    }
    connectedCallback() {
        super.connectedCallback();
        this.setAttribute('role', 'option');
        this.setAttribute('aria-disabled', `${this.disabled}`);
    }
    async willUpdate(changedProperties) {
        if (changedProperties.has('selected')) {
            this.setAttribute('aria-selected', `${this.selected}`);
        }
    }
    /**
     * Dispatch v-select event to handle if selection is made by setting the selected attribute on menu item
     *
     * Only dispatch the event if the property was changed from valid value (i.e. not undefined)
     */
    updated(changedProperties) {
        if (changedProperties.get('selected') !== undefined) {
            const customEvent = new CustomEvent(MenuEvent.Select, {
                bubbles: true,
                cancelable: false,
                composed: true,
                detail: { item: this, preventChangeEvent: true },
            });
            this.dispatchEvent(customEvent);
        }
    }
    getTextLabel() {
        return this.textContent ? this.textContent : '';
    }
    handleSlotChange() {
        const label = this.getTextLabel();
        if (this.cachedTextLabel === undefined) {
            this.cachedTextLabel = label;
            return;
        }
        if (this.cachedTextLabel !== label) {
            this.cachedTextLabel = label;
            const customEvent = new CustomEvent(MenuEvent.LabelChange, {
                bubbles: true,
                cancelable: false,
                composed: true,
                detail: { item: this },
            });
            this.dispatchEvent(customEvent);
        }
    }
    renderCheckMark() {
        if (this.checkMark && this.selected) {
            return html ` <svg
        class="menu-item__check-mark"
        viewBox="0 0 21 21"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="m7.876 14.149-3.65-3.65-1.242 1.235 4.892 4.89 10.5-10.5-1.234-1.233-9.266 9.258Z"
          fill="currentColor"
          stroke="currentColor"
          stroke-width=".25"
        />
      </svg>`;
        }
        return nothing;
    }
    render() {
        const classNames = classMap({
            'menu-item': true,
            'menu-item--check-mark': this.checkMark,
            'menu-item--disabled': this.disabled,
            'menu-item--secondary': this.secondary,
            'menu-item--selected': this.selected,
            [`menu-item--${this.size}`]: true,
        });
        return html `
      <div class=${classNames}>
        <slot name="prefix" part="prefix" class="menu-item__prefix"></slot>
        ${this.renderCheckMark()}
        <slot @slotchange=${this.handleSlotChange}></slot>
        <slot name="suffix" part="suffix" class="menu-item__suffix"></slot>
      </div>
    `;
    }
};
ViedocMenuItem.styles = [
    menuItemStyle,
    css `
      :host(:focus) {
        outline: none;
      }

      .menu-item--check-mark {
        padding-inline-start: 2.25rem; /* 36px */
      }

      .menu-item__check-mark {
        width: 1.313rem; /* 21px */
        height: 1.313rem;
        position: absolute;
        left: 0.625rem; /* 10px */
      }

      .menu-item--selected {
        background: var(--color-gray-15);
      }

      .menu-item--small {
        font-size: var(--font-size-xs);
        line-height: var(--line-height-looser);
        min-height: 0.75rem; /* 36px incl margin and padding */
      }

      .menu-item--secondary {
        color: var(--color-gray-30);
      }

      .menu-item--disabled {
        pointer-events: none;
        color: var(--secondary-text-color);
        background: var(--color-gray-5) !important;

        margin: 0;
        padding: 0.75rem 1rem; /* menu item margin + menu item vertical padding = 0.75rem */
        border-radius: 0;
      }

      .menu-item--disabled.menu-item--check-mark {
        padding-inline-start: 2.625rem; /* 42px */
      }

      .menu-item--disabled.menu-item--check-mark.menu-item--selected::before {
        left: 1rem;
      }

      .menu-item__prefix,
      .menu-item__suffix {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
      }

      .menu-item__suffix {
        margin-inline-start: auto;
      }

      .menu-item__prefix::slotted(*) {
        margin-inline-end: 0.5rem;
        height: 1.313rem; /* 21px */
      }

      .menu-item__suffix::slotted(*) {
        height: 1.313rem; /* 21px */
      }

      .menu-item--small .menu-item__prefix::slotted(*) {
        margin-inline-end: 0.4rem;
        height: 1.125rem; /* 18px */
      }

      .menu-item--small .menu-item__suffix::slotted(*) {
        height: 1.125rem; /* 18px */
      }
    `,
];
__decorate([
    property({ type: Boolean, attribute: 'check-mark' })
], ViedocMenuItem.prototype, "checkMark", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ViedocMenuItem.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ViedocMenuItem.prototype, "secondary", void 0);
__decorate([
    property({ type: Boolean })
], ViedocMenuItem.prototype, "selected", void 0);
__decorate([
    property({ type: String })
], ViedocMenuItem.prototype, "size", void 0);
__decorate([
    property({ type: String })
], ViedocMenuItem.prototype, "value", void 0);
__decorate([
    queryAssignedElements({ slot: 'prefix' })
], ViedocMenuItem.prototype, "prefixContent", void 0);
__decorate([
    queryAssignedElements({ slot: 'suffix' })
], ViedocMenuItem.prototype, "suffixContent", void 0);
ViedocMenuItem = __decorate([
    customElement('viedoc-menu-item')
], ViedocMenuItem);
export { ViedocMenuItem };
